import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Columns3, Loader2, Plus, Trash2 } from "lucide-react";
import {
  WORKFLOW_RUN_COLUMN_LIMIT,
  type WorkflowRunColumn,
} from "@proclaw/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { workflowsApi, workflowRunsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";

/**
 * Editor for a workflow's custom run columns.
 *
 * Each column pulls a value out of the run's own data via a dot-path — the
 * same path grammar condition nodes use. The source input is backed by a
 * datalist of paths discovered from recent runs, because expecting someone to
 * know the exact shape of their trigger payload and type it blind is how this
 * feature would go unused.
 *
 * Saving triggers a server-side backfill across existing runs, so a newly
 * defined column is populated for history rather than only for future runs.
 */
export function RunColumnsDialog({
  workflowId,
  initialColumns,
  onClose,
}: {
  workflowId: string;
  initialColumns: WorkflowRunColumn[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [columns, setColumns] = useState<WorkflowRunColumn[]>(initialColumns);
  const [error, setError] = useState<string | null>(null);

  const { data: pathData } = useQuery({
    queryKey: ["workflow-run-field-paths", workflowId],
    queryFn: () => workflowRunsApi.fieldPaths(workflowId),
  });

  const save = useMutation({
    mutationFn: () => workflowsApi.update(workflowId, { runColumns: columns }),
    onSuccess: () => {
      // The runs list caches rows including their column values, and the
      // workflow detail caches the definitions — both are stale now.
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowRuns.byWorkflow(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.detail(workflowId),
      });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not save columns");
    },
  });

  const update = (i: number, patch: Partial<WorkflowRunColumn>) => {
    setColumns((cols) =>
      cols.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    );
  };

  const add = () => {
    setColumns((cols) => [...cols, { key: "", label: "", source: "" }]);
  };

  // Mirrors the server's validation so problems surface before a round-trip.
  const duplicateKeys = new Set(
    columns
      .map((c) => c.key)
      .filter((k, i, arr) => k.length > 0 && arr.indexOf(k) !== i),
  );
  const incomplete = columns.some(
    (c) => !c.key.trim() || !c.label.trim() || !c.source.trim(),
  );
  const badSource = columns.some(
    (c) =>
      c.source.trim().length > 0 &&
      !c.source.startsWith("inputs.") &&
      !c.source.startsWith("steps."),
  );
  const canSave =
    !incomplete && duplicateKeys.size === 0 && !badSource && !save.isPending;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-[min(720px,92vw)] overflow-y-auto rounded-xl border border-border bg-background p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center gap-2">
          <Columns3 size={16} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Custom run columns</h2>
        </div>
        <p className="mb-4 text-xs text-text-muted">
          Surface a value from each run — a case number, a ticket ref — as its
          own column, then search and sort the runs list on it. Saving
          backfills existing runs.
        </p>

        {columns.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-surface/50 px-3 py-6 text-center text-xs text-text-muted">
            No custom columns yet.
          </div>
        )}

        <div className="space-y-3">
          {columns.map((col, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-surface p-3"
            >
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <Field label="Heading">
                  <Input
                    value={col.label}
                    placeholder="Case number"
                    onChange={(e) => {
                      const label = e.target.value;
                      // Derive the key from the heading on first entry so the
                      // common path is one field, not two. Once a key exists
                      // it's left alone — it's the identity the stored values
                      // and any saved sort are keyed by.
                      update(i, {
                        label,
                        ...(col.key
                          ? {}
                          : {
                              key: label
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/^-+|-+$/g, "")
                                .slice(0, 60),
                            }),
                      });
                    }}
                  />
                </Field>
                <Field label="Key">
                  <Input
                    value={col.key}
                    placeholder="case-number"
                    onChange={(e) => update(i, { key: e.target.value })}
                    className={
                      duplicateKeys.has(col.key) ? "border-rose-500" : undefined
                    }
                  />
                </Field>
              </div>
              <div className="mt-2">
                <Field label="Source path">
                  <Input
                    list="workflow-run-field-paths"
                    value={col.source}
                    placeholder="inputs.caseNumber"
                    onChange={(e) => update(i, { source: e.target.value })}
                  />
                </Field>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-[11px] text-text-muted">
                  Starts with <code>inputs.</code> or <code>steps.</code>
                </p>
                <button
                  onClick={() =>
                    setColumns((cols) => cols.filter((_, idx) => idx !== i))
                  }
                  className="flex items-center gap-1 text-[11px] text-text-muted transition-colors hover:text-rose-600"
                >
                  <Trash2 size={11} />
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* One shared datalist — the browser matches it by id from each input. */}
        <datalist id="workflow-run-field-paths">
          {(pathData?.paths ?? []).map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        {columns.length < WORKFLOW_RUN_COLUMN_LIMIT && (
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            className="mt-3 gap-1.5"
          >
            <Plus size={12} />
            Add column
          </Button>
        )}
        {columns.length >= WORKFLOW_RUN_COLUMN_LIMIT && (
          <p className="mt-3 text-[11px] text-text-muted">
            Limit of {WORKFLOW_RUN_COLUMN_LIMIT} columns reached.
          </p>
        )}

        {duplicateKeys.size > 0 && (
          <Problem>Column keys must be unique.</Problem>
        )}
        {badSource && (
          <Problem>
            Source paths must start with <code>inputs.</code> or{" "}
            <code>steps.</code>
          </Problem>
        )}
        {error && <Problem>{error}</Problem>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            className="gap-1.5"
          >
            {save.isPending && <Loader2 size={12} className="animate-spin" />}
            Save &amp; backfill
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
      {children}
    </p>
  );
}
